import React, { useState } from 'react';
import { Mail, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      setSubmitStatus({
        type: 'success',
        message: response.data.message
      });
      
      // Reset form after successful submission
      setTimeout(() => {
        setFormData({ name: '', email: '', company: '', message: '' });
        setSubmitStatus(null);
      }, 5000);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({
        type: 'error',
        message: error.response?.data?.detail || 'Failed to submit form. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-[#0f3f5d] mb-4">
            Let's Start a Conversation
          </h2>
          <p className="text-xl text-gray-600">
            Ready to expand across borders? Get in touch with our team to discuss your international business goals.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info Cards */}
          <div className="space-y-6">
            <Card className="border-l-4 border-l-[#508f9d] hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-[#508f9d]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#508f9d]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0f3f5d] mb-1">Email Us</h3>
                    <p className="text-gray-600 text-sm">info@jcanresourcesllc.com</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-[#c2983e] hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-[#c2983e]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#c2983e]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0f3f5d] mb-1">Visit Us</h3>
                    <p className="text-gray-600 text-sm">Los Angeles, CA, USA</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Business Hours */}
            <div className="bg-gradient-to-br from-[#0f3f5d] to-[#508f9d] rounded-xl p-6 text-white">
              <h3 className="font-semibold mb-3">Business Hours</h3>
              <div className="space-y-2 text-sm opacity-90">
                <p>Monday - Friday: 9:00 AM - 6:00 PM EST</p>
                <p>Saturday - Sunday: Closed</p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="shadow-xl border-t-4 border-t-[#c2983e]">
              <CardContent className="p-8">
                {submitStatus?.type === 'success' ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4">
                      <CheckCircle className="w-12 h-12 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#0f3f5d] mb-2">Message Sent!</h3>
                    <p className="text-gray-600 text-center">
                      {submitStatus.message}
                    </p>
                  </div>
                ) : (
                  <>
                    {submitStatus?.type === 'error' && (
                      <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                        <AlertCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                        <p className="text-red-800 text-sm">{submitStatus.message}</p>
                      </div>
                    )}
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-[#0f3f5d] mb-2">
                          Full Name *
                        </label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="John Doe"
                          className="border-gray-300 focus:border-[#508f9d] focus:ring-[#508f9d]"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-[#0f3f5d] mb-2">
                          Email Address *
                        </label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="john@company.com"
                          className="border-gray-300 focus:border-[#508f9d] focus:ring-[#508f9d]"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-[#0f3f5d] mb-2">
                        Company Name
                      </label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your Company"
                        className="border-gray-300 focus:border-[#508f9d] focus:ring-[#508f9d]"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-[#0f3f5d] mb-2">
                        Message *
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        placeholder="Tell us about your project and how we can help..."
                        className="border-gray-300 focus:border-[#508f9d] focus:ring-[#508f9d]"
                      />
                    </div>

                    <Button 
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#8b2e4a] hover:bg-[#6d2339] text-white text-lg py-6 group disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                      <Send className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </form>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
